'use client';
import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import compact from 'lodash/compact';
import {
  useAuth,
} from '@/contexts/AuthContext';
import {
  useGetOrCreateUserSettings,
} from '@/hooks/useApi';
import {
  UserSettingDTO,
} from '@/dtos/userSettingDTO';
import FullScreenSpinner from '@/core/components/FullScreenSpinner';

type UserSettingsContextValue = {
  settings?: UserSettingDTO;
  providerIds: string[];
  setProviderIds: (next: string[]) => void;
};

const UserSettingsContext = createContext<UserSettingsContextValue>({
  settings: undefined,
  providerIds: [],
  setProviderIds: () => {},
});

export function UserSettingsProvider({ children }: PropsWithChildren) {
  const { isAuthenticated } = useAuth();
  const { data: settings, isLoading } = useGetOrCreateUserSettings(isAuthenticated);
  const [providerIds, setProviderIdsState] = useState<string[]>([]);

  useEffect(() => {
    try {
      const pref = settings?.preferenceJson ?? '{}';
      const parsed = JSON.parse(pref ?? '{}') as Record<string, unknown>;
      const ids = Array.isArray(parsed?.selectedProviderIds)
        ? (parsed?.selectedProviderIds)
        : [];
      const normalized = Array.from(new Set(compact(ids).map(String)));
      setProviderIdsState(normalized);
    } catch {
      // ignore parse errors
    }
  }, [settings?.preferenceJson]);

  const setProviderIds = (next: string[]) => {
    setProviderIdsState(next);
  };

  const value = useMemo<UserSettingsContextValue>(() => ({
    settings,
    providerIds,
    setProviderIds,
  }), [
    settings,
    providerIds,
    setProviderIds,
    isLoading,
  ]);

  return (
    <UserSettingsContext.Provider value={value}>
      {isLoading ? <FullScreenSpinner /> : children}
    </UserSettingsContext.Provider>
  );
}

export function useUserSettings() {
  const ctx = useContext(UserSettingsContext);
  return ctx;
}
